<template lang="pug">
div(v-if="orderSummary")
  table.table
    thead
      tr
        th Artikelnummer
        th Name
        th Variante
        th Menge
        th(v-if="editable")
    tbody
      template(v-for="(product, px) in availableProducts")
        tr
          td {{ product.modelNumber }}
          td {{ productName(product) }}
          td
            template(v-if="product.variantNumber")
              | {{ product.variantName }} ({{ product.variantNumber }})
          td.input-container
            template(v-if="editable")
              input.form-control.form-control-sm(
                type="text"
                inputmode="numeric" pattern="[0-9]*"
                v-model.number="productQuantities[product.productId]"
                :max="maxQuantity(product)"
                :class="{ 'validate-minimum-quantity': productQuantities[product.productId] > 0 && productQuantities[product.productId] < product.minimalOrderQuantity }"
              )
              div.minimum-quantity(v-if="product.minimalOrderQuantity")
                | {{ product.minimalOrderQuantity }}
            template(v-else)
              | {{ product.quantity }}
          td.align-middle(v-if="editable")
            button.btn.btn-danger.btn-sm.mr-2(@click="removeProduct(product)")
              svg(style="width:16px;height:16px" viewBox="0 0 24 24")
                path(fill="currentColor" d="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8.46,11.88L9.87,10.47L12,12.59L14.12,10.47L15.53,11.88L13.41,14L15.53,16.12L14.12,17.53L12,15.41L9.88,17.53L8.47,16.12L10.59,14L8.46,11.88M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z")

  div.unavailable-section(v-if="unavailableProducts.length > 0")
    h4.mt-4 Nicht mehr auf Lager
    p.text-muted.small Diese Artikel werden nicht mit dieser Bestellung übermittelt.
    table.table.table-sm
      thead
        tr
          th Artikelnummer
          th Name
          th Variante
          th Menge
          th(v-if="editable")
      tbody
        tr.unavailable-row(v-for="(product, px) in unavailableProducts" :key="product.productId")
          td {{ product.modelNumber }}
          td {{ productName(product) }}
          td
            template(v-if="product.variantNumber")
              | {{ product.variantName }} ({{ product.variantNumber }})
          td {{ product.quantity }}
          td.align-middle(v-if="editable")
            button.btn.btn-danger.btn-sm.mr-2(@click="removeProduct(product)")
              svg(style="width:16px;height:16px" viewBox="0 0 24 24")
                path(fill="currentColor" d="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8.46,11.88L9.87,10.47L12,12.59L14.12,10.47L15.53,11.88L13.41,14L15.53,16.12L14.12,17.53L12,15.41L9.88,17.53L8.47,16.12L10.59,14L8.46,11.88M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z")

</template>

<script>
import debounce from 'lodash.debounce'
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'OrderSummary',
  props: {
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    orderSummary: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data: () => ({
    allProductIds: new Set()
  }),
  computed: {
    ...mapState('orderForm', [
      'productQuantities'
    ]),
    ...mapGetters('orderForm', [
      'formHasError',
      'orderForm',
      'orderedProducts'
    ]),
    ...mapGetters('authentication', [
      'customer'
    ]),
    // Split keyed on availabilityReason: legacy converter paths leave it
    // empty so historical orders fall through to the orderable table; the
    // OOS section is scoped to stock issues only so blocked / no_price /
    // no_permission items (which should already be stripped by cart cleanup)
    // are not mislabeled as "Nicht mehr auf Lager".
    availableProducts () {
      var list = this.orderSummary && this.orderSummary.orderProductsList
      return list ? list.filter(p => !p.availabilityReason) : []
    },
    unavailableProducts () {
      var list = this.orderSummary && this.orderSummary.orderProductsList
      return list ? list.filter(p => p.availabilityReason === 'out_of_stock') : []
    }
  },
  watch: {
    productQuantities: {
      handler () {
        Object.keys(this.orderedProducts).forEach(k => {
          this.allProductIds.add(k)
        })
        // ensure that we have all of the product ids set to 0 in case they were set to null
        let fix = false
        this.allProductIds.forEach(pid => {
          if (this.productQuantities[pid] === null || this.productQuantities[pid] === undefined || this.productQuantities[pid] === '') {
            this.productQuantities[pid] = '0'
            fix = true
          }
          if (this.customer.specialOffersList) {
            for (let i = 0; i < this.customer.specialOffersList.length; i++) {
              if (this.customer.specialOffersList[i].product.id === pid && this.productQuantities[pid] > this.customer.specialOffersList[i].maxQuantityPerCustomer) {
                this.productQuantities[pid] = this.customer.specialOffersList[i].maxQuantityPerCustomer
              }
            }
          }
        })
        // if we did a fix of product quantities, return early because the watch will trigger again and do the refresh
        if (fix) {
          return
        }
        if (!this.formHasError) {
          this.refreshOrderSummary()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    ...mapActions('orderForm', [
      'updateOrderSummary'
    ]),
    ...mapActions('ui', [
      'newOrder'
    ]),
    maxQuantity (product) {
      if (this.customer.specialOffersList) {
        for (let i = 0; i < this.customer.specialOffersList.length; i++) {
          if (this.customer.specialOffersList[i].product.id === product.productId) {
            return this.customer.specialOffersList[i].maxQuantityPerCustomer
          }
        }
      }
      return 1000
    },
    productName (product) {
      var name = ''
      if (product.specialOffer) {
        name = '✨ '
      }
      name = name + product.nameDe
      if (product.specialOffer && (product.specialOffer.removesShippingCosts || !product.specialOffer.onlineDiscountApplicable)) {
        name += ' ('
        if (product.specialOffer.removesShippingCosts) {
          name += 'versandkostenfrei'
        }
        if (product.specialOffer.removesShippingCosts && !product.specialOffer.onlineDiscountApplicable) {
          name += ' & '
        }
        if (!product.specialOffer.onlineDiscountApplicable) {
          name += 'kein Online-Rabatt'
        }
        name += ')'
      }
      return name
    },
    refreshOrderSummary: debounce(async function () {
      let ok = await this.updateOrderSummary()
      if (!ok) {
        this.newOrder()
      }
    }, 1000, { leading: false }),
    removeProduct (product) {
      if (this.productQuantities[product.productId] === '0') {
        this.allProductIds.delete(product.productId)
        this.productQuantities[product.productId] = null
      } else {
        this.productQuantities[product.productId] = '0'
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
  input.form-control
    height 35px
    width 60px
    margin 0!important
    text-align center
    padding 0px

  div.minimum-quantity
    display none

  input.validate-minimum-quantity
    border 2px solid var(--danger)

  input.validate-minimum-quantity + div.minimum-quantity
    display flex
    position relative
    border 2px solid var(--danger)
    top -43px
    left -8px
    border-radius 50%
    width 20px
    height: 20px
    align-items center
    justify-content center
    background white
    color var(--danger)
    font-size 80%

  @media (max-width 640px)
    thead
      font-size 80%

  @media (max-width 360px)
    thead tr td:last-child
    tbody tr td:last-child
      display none

    tbody
      font-size 80%

  .unavailable-section
    h4
      color #BEADA2

  .unavailable-row
    color #888
    font-style italic

</style>
